'use client';

import React from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useHorizontalScroll } from '@benzinga/hooks';
import Image from 'next/image';
import type { TrendingTopic } from '@benzinga/trending-topics-manager';

export interface QuickLinkItem extends TrendingTopic {
  logo?: string;
}

export interface QuicklinksProps {
  quicklinks: QuickLinkItem[];
  title?: string;
}

export const Quicklinks: React.FC<QuicklinksProps> = ({ quicklinks, title = 'QUICK LINKS' }) => {
  const { scrollLeft, scrollRef, scrollRight } = useHorizontalScroll<HTMLUListElement>();

  if (!Array.isArray(quicklinks) || !quicklinks.length) return null;

  return (
    <div className="quicklinks-container flex items-center w-full bg-white border border-gray-200 rounded-lg pr-4 gap-4 h-[60px] md:pr-4 max-md:p-2">
      <div className="hidden md:flex items-center gap-4 shrink-0 h-full pl-4 pr-4 bg-[#f2f8fe] rounded-l-lg">
        <h2 className="font-bold text-sm text-slate-900 whitespace-nowrap">{title}</h2>
      </div>

      <div className="flex-1 overflow-hidden relative">
        <ul
          className="flex gap-3 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          ref={scrollRef}
        >
          {quicklinks.map((item, index) => {
            const href = item.url_public ? `/${item.url_public}` : `/quote/${item.name}`;

            return (
              <li className="shrink-0" key={`${item.name}-${index}`}>
                <a
                  className="flex items-center gap-2 bg-[#f2f8ff] px-3 py-1 rounded-full no-underline text-slate-700 text-[13px] font-semibold h-8 transition-colors duration-200 hover:bg-[#e2efff] hover:text-slate-900"
                  href={href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {item.logo && (
                    <Image
                      alt={`${item.name} logo`}
                      className="w-4 h-4 rounded-full object-cover"
                      height={16}
                      priority
                      src={item.logo}
                      width={16}
                    />
                  )}
                  <h3 className="text-[13px]">{item.name}</h3>
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="hidden md:flex gap-2 shrink-0">
        <button
          aria-label="Scroll quick links left"
          className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-50 text-slate-500 border-none cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:text-slate-900"
          onClick={scrollLeft}
        >
          <FaChevronLeft size={10} />
        </button>
        <button
          aria-label="Scroll quick links right"
          className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-50 text-slate-500 border-none cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:text-slate-900"
          onClick={scrollRight}
        >
          <FaChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};
